import { useEffect, useMemo, useState } from "react";
import { MapPin, Plus, X, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// JS Date#getDay() is 0=Sunday..6=Saturday; our weekday convention
// (DAYS above, and the backend) is 0=Monday..6=Sunday.
const isoWeekday = (dateStr) => (new Date(`${dateStr}T00:00:00`).getDay() + 6) % 7;

const norm = (s) => s.trim().toLowerCase().replace(/\s+/g, " ");
const addUnique = (list, s) => list.some((x) => norm(x) === norm(s)) ? list : [...list, s];

function SuburbChips({ suburbs, onRemove, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1">
      {suburbs.map((s) => (
        <span key={s} className="inline-flex items-center gap-1 rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200 text-xs px-2 py-0.5">
          {s}
          {onRemove && (
            <button data-testid={`${testIdPrefix}-remove-${s}`} onClick={() => onRemove(s)} className="hover:text-rose-600">
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      {suburbs.length === 0 && <span className="text-xs text-slate-400 italic">none</span>}
    </div>
  );
}

function AddSuburbInput({ onAdd, testId }) {
  const [v, setV] = useState("");
  const submit = () => {
    const name = v.trim();
    if (!name) return;
    onAdd(name);
    setV("");
  };
  return (
    <div className="flex items-center gap-1 mt-1">
      <input value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        placeholder="Add suburb…"
        data-testid={testId} className="w-32 rounded-lg border border-slate-300 px-2 py-1 text-xs" />
      <button type="button" onClick={submit} data-testid={`${testId}-btn`} className="text-xs font-semibold text-cyan-700 hover:underline">Add</button>
    </div>
  );
}

function MatrixRow({ row, technicians, fullyBookedAll, onUpdate, onToggleActive, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    technician_id: row.technician_id, weekday: row.weekday, area_name: row.area_name,
  });

  // Feb 2026 — `fullyBookedAll` is fetched ONCE for the whole matrix (see
  // LocationDayMatrix below) from the general, suburb-agnostic diagnostic —
  // a technician's own calendar being full on a date has nothing to do
  // with which suburb is being checked, so no per-row API call (and no
  // GEO/Google Routes work, which that per-row call used to trigger) is
  // needed here. Just scope the shared list to this row's technician +
  // weekday and de-dupe same-day double-slot entries.
  const fullyBooked = useMemo(() => {
    if (!row.active) return [];
    const seen = new Set();
    const out = [];
    for (const f of fullyBookedAll) {
      if (f.technician_id !== row.technician_id) continue;
      if (isoWeekday(f.date) !== row.weekday || seen.has(f.date)) continue;
      seen.add(f.date);
      out.push(f);
    }
    return out;
  }, [fullyBookedAll, row.active, row.technician_id, row.weekday]);

  const startEdit = () => {
    setForm({ technician_id: row.technician_id, weekday: row.weekday, area_name: row.area_name });
    setEditing(true);
  };

  const save = () => {
    onUpdate(row.id, form);
    setEditing(false);
  };

  return (
    <div data-testid={`location-day-row-${row.id}`}
      className={`rounded-xl border px-4 py-3 ${row.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}>
      {row.warnings?.length > 0 && (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Overlaps {row.warnings.map((w) => w.overlapping_suburbs.join(", ")).join("; ")} already
          assigned to another technician on this day — both will show as eligible.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {editing ? (
          <>
            <select value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })}
              data-testid={`location-day-edit-tech-${row.id}`} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm">
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
              data-testid={`location-day-edit-weekday-${row.id}`} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <input value={form.area_name} onChange={(e) => setForm({ ...form, area_name: e.target.value })}
              data-testid={`location-day-edit-area-${row.id}`} className="rounded-lg border border-slate-300 px-2 py-1 text-sm flex-1 min-w-[10rem]" />
            <button data-testid={`location-day-save-${row.id}`} onClick={save} className="text-xs font-semibold text-emerald-700 hover:underline">Save</button>
            <button onClick={() => setEditing(false)} className="text-xs font-semibold text-slate-500 hover:underline">Cancel</button>
          </>
        ) : (
          <>
            <span className="font-bold text-slate-900 text-sm">{row.technician_name || "Unknown"}</span>
            <span className="text-xs font-bold uppercase tracking-wide text-cyan-700 bg-cyan-50 rounded-full px-2 py-0.5">{DAYS[row.weekday]}</span>
            <span className="text-sm text-slate-700">{row.area_name}</span>
            {fullyBooked.length > 0 && (
              <span data-testid={`location-day-fully-booked-${row.id}`}
                title={`Fully booked: ${fullyBooked.map((f) => f.date).join(", ")}`}
                className="text-[10px] font-bold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                Fully booked ({fullyBooked.length})
              </span>
            )}
            <span className="flex-1" />
            <button data-testid={`location-day-edit-${row.id}`} onClick={startEdit} className="text-xs font-semibold text-cyan-700 hover:underline">Edit</button>
            <button data-testid={`location-day-toggle-${row.id}`} onClick={() => onToggleActive(row.id, !row.active)}
              className="text-xs font-semibold text-slate-600 hover:underline">{row.active ? "Deactivate" : "Activate"}</button>
            <button data-testid={`location-day-delete-${row.id}`}
              onClick={() => window.confirm(`Delete "${row.area_name}"? This stops exposing its suburbs for ${row.technician_name} on ${DAYS[row.weekday]}.`) && onDelete(row.id)}
              className="text-xs font-semibold text-rose-600 hover:underline">Delete</button>
          </>
        )}
      </div>
      <div className="mt-2">
        <SuburbChips suburbs={row.suburbs}
          onRemove={(s) => onUpdate(row.id, { suburbs: row.suburbs.filter((x) => x !== s) })}
          testIdPrefix={`location-day-row-${row.id}`} />
        <AddSuburbInput testId={`location-day-add-suburb-${row.id}`}
          onAdd={(s) => onUpdate(row.id, { suburbs: addUnique(row.suburbs, s) })} />
      </div>
    </div>
  );
}

export default function LocationDayMatrix() {
  const [rows, setRows] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [fullyBookedAll, setFullyBookedAll] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [form, setForm] = useState({ technician_id: "", weekday: "0", area_name: "" });
  const [newSuburbs, setNewSuburbs] = useState([]);

  useEffect(() => {
    api.get("/admin/location-day-matrix").then(({ data }) => {
      setRows(data.rows || []);
      setTechnicians(data.technicians || []);
      setForm((f) => ({ ...f, technician_id: f.technician_id || data.technicians?.[0]?.id || "" }));
    }).catch(() => toast.error("Failed to load the location-day matrix"));
    api.get("/admin/location-day-matrix/unmapped").then(({ data }) => setUnmapped(data.suburbs || []))
      .catch(() => {});
    // Feb 2026 — ONE shared, suburb-agnostic call for every row's "Fully
    // booked" badge (see MatrixRow) instead of one GEO/route-scoring call
    // per row — this endpoint never touches geo_route_engine/Google
    // Routes at all, it only reads each technician's own busy calendar.
    api.get("/admin/tank-water-health-check/availability-debug")
      .then(({ data }) => setFullyBookedAll(data.fully_booked || []))
      .catch(() => setFullyBookedAll([]));
  }, [refresh]);

  const update = (id, patch) => {
    api.put(`/admin/location-day-matrix/${id}`, patch).then(({ data }) => {
      if (data.warnings?.length) {
        toast.warning(`Saved — but overlaps another technician's assignment for ${data.warnings[0].overlapping_suburbs.join(", ")} on ${DAYS[data.weekday]}`);
      } else {
        toast.success("Updated");
      }
      setRefresh((x) => x + 1);
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to update"));
  };

  const toggleActive = (id, active) => {
    api.post(`/admin/location-day-matrix/${id}/active`, { active })
      .then(() => { toast.success(active ? "Reactivated" : "Deactivated — its suburbs no longer show this technician/day"); setRefresh((x) => x + 1); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };

  const del = (id) => {
    api.delete(`/admin/location-day-matrix/${id}`)
      .then(() => { toast.success("Deleted"); setRefresh((x) => x + 1); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };

  const create = (e) => {
    e.preventDefault();
    if (!form.technician_id || !form.area_name.trim() || newSuburbs.length === 0) {
      toast.error("Pick a technician, a weekday, an area name, and at least one suburb");
      return;
    }
    api.post("/admin/location-day-matrix", {
      technician_id: form.technician_id, weekday: Number(form.weekday),
      area_name: form.area_name.trim(), suburbs: newSuburbs,
    }).then(({ data }) => {
      if (data.warnings?.length) {
        toast.warning(`Created — but overlaps another technician's assignment for ${data.warnings[0].overlapping_suburbs.join(", ")} on ${DAYS[data.weekday]}`);
      } else {
        toast.success("Assignment created");
      }
      setForm({ technician_id: form.technician_id, weekday: "0", area_name: "" });
      setNewSuburbs([]);
      setRefresh((x) => x + 1);
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to create"));
  };

  const sorted = [...rows].sort((a, b) => a.weekday - b.weekday || a.technician_name?.localeCompare(b.technician_name || "") || 0);

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid="location-day-matrix-card">
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-1 flex items-center gap-1.5">
        <MapPin className="w-4 h-4" /> Technician location-day matrix
      </h2>
      <p className="text-xs text-slate-500 mb-3 max-w-2xl">
        A hard gate on top of the weekly roster above — a technician can be rostered to work a day
        without every suburb being offered that day. A public appointment only shows when the
        customer's suburb is assigned to that technician on that weekday. Changes here apply to new
        availability and future rescheduling only — already-booked appointments are never moved.
      </p>

      {unmapped.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4" data-testid="location-day-unmapped-card">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /> {unmapped.length} supported suburbs need a day assignment
          </div>
          <p className="text-xs text-amber-800 mb-2">
            No public availability shows for these yet (nothing is guessed) — assign each one to a
            technician and weekday below.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unmapped.map((s) => (
              <button key={s} data-testid={`location-day-unmapped-${s}`}
                onClick={() => setNewSuburbs((cur) => addUnique(cur, s))}
                className="text-xs rounded-full border border-amber-300 bg-white text-amber-800 px-2 py-0.5 hover:bg-amber-100">
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {sorted.map((row) => (
          <MatrixRow key={row.id} row={row} technicians={technicians} fullyBookedAll={fullyBookedAll}
            onUpdate={update} onToggleActive={toggleActive} onDelete={del} />
        ))}
        {rows.length === 0 && <div className="text-xs text-slate-400 italic">No location-day assignments yet</div>}
      </div>

      <form onSubmit={create} className="rounded-xl border border-dashed border-slate-300 p-3" data-testid="location-day-create-form">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <select value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })}
            aria-label="Technician" data-testid="location-day-new-tech" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })}
            aria-label="Weekday" data-testid="location-day-new-weekday" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <input value={form.area_name} onChange={(e) => setForm({ ...form, area_name: e.target.value })}
            placeholder="Area name, e.g. Noosa & Coastal Beaches"
            data-testid="location-day-new-area" className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm flex-1 min-w-[12rem]" />
          <button type="submit" data-testid="location-day-create-btn"
            className="rounded-full bg-cyan-700 text-white text-sm font-bold px-4 py-1.5 hover:bg-cyan-800 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Create
          </button>
        </div>
        <SuburbChips suburbs={newSuburbs} onRemove={(s) => setNewSuburbs(newSuburbs.filter((x) => x !== s))}
          testIdPrefix="location-day-new" />
        <AddSuburbInput testId="location-day-new-suburb-input" onAdd={(s) => setNewSuburbs((cur) => addUnique(cur, s))} />
      </form>
    </div>
  );
}
